import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"

/**
 * FancyYool Design System — Badge Variants
 *
 * Cada variante usa los colores exactos de la paleta FancyYool.
 * Nunca usar emerald/amber/blue genéricos de Tailwind — siempre este sistema.
 *
 * Paleta de referencia:
 *  teal/greenchill   #007D6E  → Marca, CTA, estados activos (pagado, aprobada, ACTIVA)
 *  orange            #D35400  → Advertencia / Pendiente
 *  red               #ef4444  → Error / Fallido / Rechazado / Expirado
 *  sky               #0ea5e9  → Datos informativos / Procesando / Suscribiendo
 *  cyan              #06b6d4  → Analytics / métricas
 *  slate             #64748b  → Metadata / IDs / Cancelado / Inactivo
 *  gray              #6b7280  → Deshabilitado
 *  violet            #5b2be5  → Premium / planes destacados
 *  indigo            #7D7CFF  → Sección Conversiones
 *  purple            #a855f7  → Sección Campañas
 *  blue              #2563EB  → Links secundarios
 *  lime/divina       #147d89  → Vertical Divina Seguros
 *  pink              #E91E63  → Alerta urgente
 *  green (unnamed)   #158B5C  → Completado alternativo
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold border transition-colors",
  {
    variants: {
      variant: {
        /* ── Base / neutros ─────────────────────────────────────────────── */
        default:     "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
        secondary:   "bg-[#64748b]/10 text-[#64748b] border-[#64748b]/25 dark:bg-[#64748b]/15 dark:text-[#94a3b8] dark:border-[#64748b]/30",
        destructive: "bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/25 dark:bg-[#ef4444]/15 dark:text-[#f87171] dark:border-[#ef4444]/30",
        outline:     "bg-transparent text-foreground border-border",

        /* ── FancyYool semánticos ────────────────────────────────────────── */
        /* success  → greenchill #007D6E   activo, completado                */
        success:     "bg-[#007D6E]/10 text-[#007D6E] border-[#007D6E]/25 dark:bg-[#007D6E]/15 dark:text-[#2dd4bf] dark:border-[#007D6E]/30",
        /* warning  → orange #D35400       advertencia, pendiente            */
        warning:     "bg-[#D35400]/10 text-[#D35400] border-[#D35400]/25 dark:bg-[#D35400]/15 dark:text-[#fb923c] dark:border-[#D35400]/30",
        /* info     → sky #0ea5e9          informativo, procesando            */
        info:        "bg-[#0ea5e9]/10 text-[#0ea5e9] border-[#0ea5e9]/25 dark:bg-[#0ea5e9]/15 dark:text-[#7dd3fc] dark:border-[#0ea5e9]/30",

        /* ── FancyYool full palette ──────────────────────────────────────── */
        teal:        "bg-[#007D6E]/10 text-[#007D6E] border-[#007D6E]/25 dark:bg-[#007D6E]/15 dark:text-[#2dd4bf] dark:border-[#007D6E]/30",
        sky:         "bg-[#0ea5e9]/10 text-[#0ea5e9] border-[#0ea5e9]/25 dark:bg-[#0ea5e9]/15 dark:text-[#7dd3fc] dark:border-[#0ea5e9]/30",
        cyan:        "bg-[#06b6d4]/10 text-[#06b6d4] border-[#06b6d4]/25 dark:bg-[#06b6d4]/15 dark:text-[#67e8f9] dark:border-[#06b6d4]/30",
        blue:        "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/25 dark:bg-[#2563EB]/15 dark:text-[#93c5fd] dark:border-[#2563EB]/30",
        indigo:      "bg-[#7D7CFF]/10 text-[#7D7CFF] border-[#7D7CFF]/25 dark:bg-[#7D7CFF]/15 dark:text-[#a5b4fc] dark:border-[#7D7CFF]/30",
        violet:      "bg-[#5b2be5]/10 text-[#5b2be5] border-[#5b2be5]/25 dark:bg-[#5b2be5]/15 dark:text-[#a78bfa] dark:border-[#5b2be5]/30",
        purple:      "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/25 dark:bg-[#a855f7]/15 dark:text-[#d8b4fe] dark:border-[#a855f7]/30",
        pink:        "bg-[#E91E63]/10 text-[#E91E63] border-[#E91E63]/25 dark:bg-[#E91E63]/15 dark:text-[#f9a8d4] dark:border-[#E91E63]/30",
        orange:      "bg-[#D35400]/10 text-[#D35400] border-[#D35400]/25 dark:bg-[#D35400]/15 dark:text-[#fb923c] dark:border-[#D35400]/30",
        red:         "bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/25 dark:bg-[#ef4444]/15 dark:text-[#f87171] dark:border-[#ef4444]/30",
        lime:        "bg-[#147d89]/10 text-[#147d89] border-[#147d89]/25 dark:bg-[#147d89]/15 dark:text-[#67e8f9] dark:border-[#147d89]/30",
        slate:       "bg-[#64748b]/10 text-[#64748b] border-[#64748b]/25 dark:bg-[#64748b]/15 dark:text-[#94a3b8] dark:border-[#64748b]/30",
        gray:        "bg-[#6b7280]/10 text-[#6b7280] border-[#6b7280]/25 dark:bg-[#6b7280]/15 dark:text-[#9ca3af] dark:border-[#6b7280]/30",
        premium:     "bg-[#5b2be5]/10 text-[#5b2be5] border-[#5b2be5]/25 dark:bg-[#5b2be5]/15 dark:text-[#a78bfa] dark:border-[#5b2be5]/30",

        /* ── Estados de pago / suscripción — FancyYool ──────────────────── */
        /* pagado    → greenchill #007D6E   aprobada, ACTIVA, paid           */
        pagado:      "bg-[#007D6E]/10 text-[#007D6E] border-[#007D6E]/25 dark:bg-[#007D6E]/15 dark:text-[#2dd4bf] dark:border-[#007D6E]/30",
        /* pendiente → orange #D35400       warning                          */
        pendiente:   "bg-[#D35400]/10 text-[#D35400] border-[#D35400]/25 dark:bg-[#D35400]/15 dark:text-[#fb923c] dark:border-[#D35400]/30",
        /* procesando → sky #0ea5e9         SUSCRIBIENDO, in-progress        */
        procesando:  "bg-[#0ea5e9]/10 text-[#0ea5e9] border-[#0ea5e9]/25 dark:bg-[#0ea5e9]/15 dark:text-[#7dd3fc] dark:border-[#0ea5e9]/30",
        /* expirado  → red #ef4444          EXPIRADA, FALLIDA, rechazado     */
        expirado:    "bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/25 dark:bg-[#ef4444]/15 dark:text-[#f87171] dark:border-[#ef4444]/30",
        /* cancelado → slate #64748b        CANCELADA, FINALIZADA, inactivo  */
        cancelado:   "bg-[#64748b]/10 text-[#64748b] border-[#64748b]/25 dark:bg-[#64748b]/15 dark:text-[#94a3b8] dark:border-[#64748b]/30",

        /* ── Legacy (no borrar — compatibilidad) ────────────────────────── */
        tomato:      "bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/25 dark:bg-[#ef4444]/15 dark:text-[#f87171] dark:border-[#ef4444]/30",
      },
    },
    defaultVariants: { variant: "default" },
  }
)

function Badge({ className, variant, ...props }) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
